import React, { useState, useEffect } from 'react';
import './StageTaskManager.css';

function StageTaskManager({ onBack }) {
  const [activeStage, setActiveStage] = useState('lead');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    default_days_after_status_change: '',
    is_recurring: false,
    recurrence_days: '',
    sort_order: 0,
    active: true,
    is_visible: true,
    show_after_task_id: null
  });

  const stages = [
    { key: 'lead', name: 'Lead Tasks', color: '#3b82f6' },
    { key: 'customer', name: 'Customer Tasks', color: '#10b981' },
    { key: 'marketer', name: 'Marketer Tasks', color: '#f59e0b' },
    { key: 'leader', name: 'Leader Tasks', color: '#8b5cf6' },
    { key: 'other', name: 'Other Tasks', color: '#6b7280' }
  ];

  useEffect(() => {
    fetchTasks();
  }, [activeStage]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/backend/api/admin/stage-tasks.php?stage=${activeStage}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setTasks(data.task_types || data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = '/backend/api/admin/stage-tasks.php?stage=' + activeStage;
      const method = editingTask ? 'PUT' : 'POST';
      
      // Clean form data - convert empty strings to null for numeric fields
      const cleanedFormData = {
        ...formData,
        default_days_after_status_change: formData.default_days_after_status_change === '' ? null : parseInt(formData.default_days_after_status_change),
        recurrence_days: formData.recurrence_days === '' ? null : parseInt(formData.recurrence_days),
        sort_order: parseInt(formData.sort_order) || 0,
        id: editingTask?.id
      };
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(cleanedFormData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setShowModal(false);
        setEditingTask(null);
        resetForm();
        fetchTasks();
      } else {
        alert('Error: ' + (data.error || 'Failed to save task'));
      }
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task. Please try again.');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      code: task.code,
      name: task.name,
      description: task.description,
      default_days_after_status_change: task.default_days_after_status_change || '',
      is_recurring: task.is_recurring,
      recurrence_days: task.recurrence_days || '',
      sort_order: task.sort_order,
      active: task.active,
      is_visible: task.is_visible !== undefined ? task.is_visible : true,
      show_after_task_id: task.show_after_task_id || null
    });
    setShowModal(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to deactivate this task?')) {
      return;
    }

    try {
      const response = await fetch('/backend/api/admin/stage-tasks.php?stage=' + activeStage, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id: taskId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        fetchTasks();
      } else {
        alert('Error: ' + (data.error || 'Failed to delete task'));
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      default_days_after_status_change: '',
      is_recurring: false,
      recurrence_days: '',
      sort_order: 0,
      active: true,
      is_visible: true,
      show_after_task_id: null
    });
    setEditingTask(null);
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTask(null);
    resetForm();
  };

  const currentStage = stages.find(s => s.key === activeStage);

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">Loading {currentStage?.name}...</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>
        <h1>Stage Task Management</h1>
        <button className="add-task-btn" onClick={openModal}>Add New Task</button>
      </div>

      <div className="stage-tabs">
        {stages.map((stage) => (
          <button 
            key={stage.key}
            className={`stage-tab ${activeStage === stage.key ? 'active' : ''}`}
            onClick={() => setActiveStage(stage.key)}
            style={{ 
              '--stage-color': stage.color,
              borderColor: activeStage === stage.key ? stage.color : 'transparent'
            }}
          >
            <div className="stage-indicator" style={{ backgroundColor: stage.color }}></div>
            {stage.name}
          </button>
        ))}
      </div>

      <div className="tasks-table-container">
        <table className="tasks-table">
          <thead>
            <tr>
              <th>Task Name</th>
              <th>Description</th>
              <th>Days After Status Change</th>
              <th>Recurring</th>
              <th>Sort Order</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.id}>
                <td className="task-name-cell">
                  <code className="task-code">{task.code}</code>
                  <div className="task-name">{task.name}</div>
                </td>
                <td className="description-cell">{task.description}</td>
                <td>
                  {task.default_days_after_status_change !== null && task.default_days_after_status_change !== undefined
                    ? `${task.default_days_after_status_change} days`
                    : 'Immediate'
                  }
                </td>
                <td>
                  {task.is_recurring && task.recurrence_days ? (
                    <span className="recurring-info">
                      Every {task.recurrence_days} days
                    </span>
                  ) : (
                    'No'
                  )}
                </td>
                <td>{task.sort_order}</td>
                <td>
                  <span className={`status-badge ${task.active ? 'active' : 'inactive'}`}>
                    {task.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button 
                    className="edit-btn"
                    onClick={() => handleEdit(task)}
                    title="Edit task"
                  >
                    Edit
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(task.id)}
                    title="Deactivate task"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tasks.length === 0 && (
          <div className="empty-state">
            <p>No {currentStage?.name.toLowerCase()} found.</p>
            <button className="add-task-btn" onClick={openModal}>Add First Task</button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingTask ? 'Edit Task' : 'Add New Task'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="task-form">
              <div className="form-group">
                <label>Task Code (unique identifier)</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="e.g., send_welcome_package"
                  required
                  pattern="[a-z_]+"
                  title="Only lowercase letters and underscores allowed"
                />
              </div>

              <div className="form-group">
                <label>Task Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Send Welcome Package"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe what this task involves. Use {contact_name} for contact name placeholder."
                  required
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Days After Status Change</label>
                  <input
                    type="number"
                    value={formData.default_days_after_status_change}
                    onChange={(e) => setFormData({...formData, default_days_after_status_change: e.target.value})}
                    min="0"
                    placeholder="Leave empty for immediate"
                  />
                  <small>How many days after contact becomes {activeStage} should this task appear?</small>
                </div>

                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                    min="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})}
                  />
                  This is a recurring task
                </label>
              </div>

              {formData.is_recurring && (
                <div className="form-group">
                  <label>Recurrence Days</label>
                  <input
                    type="number"
                    value={formData.recurrence_days}
                    onChange={(e) => setFormData({...formData, recurrence_days: e.target.value})}
                    min="1"
                    placeholder="e.g., 7 for weekly"
                  />
                  <small>How often should this task repeat?</small>
                </div>
              )}

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  />
                  Active
                </label>
              </div>

              <div className="visibility-section">
                <h3>Task Visibility Settings</h3>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_visible}
                      onChange={(e) => setFormData({...formData, is_visible: e.target.checked, show_after_task_id: e.target.checked ? null : formData.show_after_task_id})}
                    />
                    Show by default (task appears immediately)
                  </label>
                </div>

                {!formData.is_visible && (
                  <div className="form-group">
                    <label>Show after completing task:</label>
                    <select
                      value={formData.show_after_task_id || ''}
                      onChange={(e) => setFormData({...formData, show_after_task_id: e.target.value ? parseInt(e.target.value) : null})}
                    >
                      <option value="">Select prerequisite task...</option>
                      {tasks
                        .filter(task => !editingTask || task.id !== editingTask.id) // Don't allow self-reference
                        .map(task => (
                          <option key={task.id} value={task.id}>
                            {task.name} ({task.code})
                          </option>
                        ))}
                    </select>
                    <small>This task will only become available after the selected task is completed.</small>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingTask ? 'Update Task' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StageTaskManager;
